import { writeFile } from "../storage/fsa.ts";
import { logForRun, moduleProgress, getRun, type ModuleProgress } from "../storage/idb.ts";
import type { ModuleId } from "../auth/config.ts";

/**
 * The two files the exporter writes about itself, at the destination root:
 * `export.log` (§14) and `export.json` (§7).
 *
 * Both are written automatically. Nothing here is ever presented to the user
 * as a choice or a step — they exist so that when something goes wrong, the
 * answer is "send us the folder" rather than asking someone to reproduce a
 * failure they cannot describe.
 *
 * Neither contains exported content. §14 is explicit that the log excludes
 * subjects, filenames, addresses and message bodies, and the manifest holds
 * counts and byte totals only. That is what makes "send us the folder" a
 * request someone can safely agree to.
 */

const LOG_FILENAME = "export.log";
const MANIFEST_FILENAME = "export.json";

export const APP_VERSION = "0.1.0";

/**
 * Rewrites export.log in full from the run's log records.
 *
 * Rewritten rather than appended because the File System Access API has no
 * append mode — and because the IndexedDB log is the authoritative history,
 * so regenerating always converges on the truth even after an interrupted
 * write.
 */
export async function writeExportLog(
    root: FileSystemDirectoryHandle,
    runId: string
): Promise<void> {
    const entries = await logForRun(runId);

    const header = [
        `M365 Personal Data Exporter — support log`,
        `app version: ${APP_VERSION}`,
        `run: ${runId}`,
        `browser: ${navigator.userAgent}`,
        `generated: ${new Date().toISOString()}`,
        "",
        "This file contains timestamps, counts and error codes only.",
        "It contains no message content, subjects, filenames or addresses.",
        ""
    ].join("\n");

    const body = entries
        .map((e) => `${e.ts} [${e.level.toUpperCase()}] ${e.module}: ${e.message}`)
        .join("\n");

    await writeFile(root, LOG_FILENAME, `${header}${body}\n`);
}

export interface ManifestModuleStats {
    items: number;
    bytes: number;
    errors: number;
    skipped: number;
    containersComplete: number;
    containersSeen: number;
}

export interface ExportManifest {
    runId: string;
    appVersion: string;
    startedAt: string;
    updatedAt: string;
    status: string;
    modules: Record<string, ManifestModuleStats>;
    totals: { items: number; bytes: number; errors: number };
}

function toStats(progress: ModuleProgress): ManifestModuleStats {
    return {
        items: progress.done,
        bytes: progress.bytes,
        errors: progress.failed,
        skipped: progress.skipped,
        containersComplete: progress.containersComplete,
        containersSeen: progress.containersSeen
    };
}

/**
 * Rewrites export.json from recorded run state.
 *
 * Safe to call repeatedly — mid-run, on pause, and at completion — so the
 * folder describes itself accurately even if the run never reaches the end.
 */
export async function writeExportManifest(
    root: FileSystemDirectoryHandle,
    runId: string,
    modules: readonly ModuleId[]
): Promise<void> {
    const run = await getRun(runId);

    const moduleStats: Record<string, ManifestModuleStats> = {};
    let items = 0;
    let bytes = 0;
    let errors = 0;

    for (const id of modules) {
        const progress = await moduleProgress(runId, id);
        moduleStats[id] = toStats(progress);
        items += progress.done;
        bytes += progress.bytes;
        errors += progress.failed;
    }

    const manifest: ExportManifest = {
        runId,
        appVersion: APP_VERSION,
        startedAt: run?.startedAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: run?.status ?? "unknown",
        modules: moduleStats,
        totals: { items, bytes, errors }
    };

    await writeFile(root, MANIFEST_FILENAME, JSON.stringify(manifest, null, 2));
}

/** Writes both artifacts. Failures here must never fail the export itself. */
export async function writeRunArtifacts(
    root: FileSystemDirectoryHandle,
    runId: string,
    modules: readonly ModuleId[]
): Promise<void> {
    try {
        await writeExportLog(root, runId);
        await writeExportManifest(root, runId, modules);
    } catch (err) {
        // Losing the support files is bad; losing the export because the
        // support files could not be written would be worse.
        console.error("failed to write run artifacts", err);
    }
}
